import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flame, Phone, MessageSquare, Calendar, UserCheck, XCircle } from "lucide-react";
import { API, apiError } from "../lib/api";
import { PageHeader, Spinner, Empty } from "../components/common";
import { scoreColor, tempColor, formatINR } from "../lib/helpers";

export default function HotLeads() {
  const [leads, setLeads] = useState(null);
  const [associates, setAssociates] = useState([]);
  const [selected, setSelected] = useState({});

  const load = () => {
    API.get("/hot-leads").then((r) => setLeads(r.data));
    API.get("/associates").then((r) => setAssociates(r.data));
  };
  useEffect(load, []);

  const assign = async (lead) => {
    const aid = selected[lead.id] || lead.recommended_associate?.id;
    if (!aid) { toast.error("Select an associate"); return; }
    try {
      await API.post(`/leads/${lead.id}/assign`, { associate_id: aid });
      toast.success(`Assigned ${lead.name} — associate notified`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const notReady = async (lead) => {
    await API.post(`/leads/${lead.id}/mark-not-ready`);
    toast.info("Returned to nurturing"); load();
  };

  if (!leads) return <Spinner />;

  return (
    <div>
      <PageHeader title="🔥 Hot Leads Queue" subtitle="Admin-only. Only you can assign a hot lead to an associate." testid="hot-leads-header" />
      {leads.length === 0 ? <Empty label="No hot leads awaiting assignment." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {leads.map((l, i) => (
            <div key={l.id} className="mb-card p-5 border-red-500/20 pulse-hot animate-fade-up" data-testid={`hot-lead-card-${i}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Flame className="h-4 w-4 text-red-400" />
                    <span className="font-heading font-semibold text-slate-100">{l.name}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{l.project_interest || "—"}</div>
                </div>
                <div className="text-right">
                  <div className={`font-heading text-3xl font-bold ${scoreColor(l.score)}`}>{l.score}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${tempColor(l.temperature)}`}>{l.temperature}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div><span className="text-slate-500">Budget:</span> <span className="text-slate-200">{l.budget || "—"}</span></div>
                <div><span className="text-slate-500">Timeline:</span> <span className="text-slate-200">{l.timeline || "—"}</span></div>
                <div><span className="text-slate-500">Intent:</span> <span className="text-slate-200 capitalize">{l.intent}</span></div>
                <div><span className="text-slate-500">Confidence:</span> <span className="text-slate-200">{l.confidence}%</span></div>
              </div>

              <div className="text-xs text-slate-300 bg-slate-900/50 border border-slate-800 rounded-lg p-2.5 mb-2">{l.ai_summary}</div>
              {l.memory?.objections?.length > 0 && <div className="text-[11px] text-red-300 mb-2">Objections: {l.memory.objections.join(", ")}</div>}
              <div className="text-[11px] text-amber-300 mb-3">💡 {l.ai_recommended_action}</div>

              <div className="text-[11px] text-slate-500 mb-1">AI recommends: <span className="text-purple-300">{l.recommended_associate?.name || "—"}</span></div>
              <select data-testid={`assign-associate-dropdown-${i}`} value={selected[l.id] || l.recommended_associate?.id || ""} onChange={(e) => setSelected({ ...selected, [l.id]: e.target.value })}
                className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 mb-3">
                <option value="">Select associate…</option>
                {associates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>

              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => assign(l)} className="btn-gold py-2 text-sm flex items-center justify-center gap-1.5" data-testid={`assign-btn-${i}`}>
                  <UserCheck className="h-4 w-4" /> Assign
                </button>
                <button onClick={() => notReady(l)} className="py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 flex items-center justify-center gap-1.5" data-testid={`not-ready-btn-${i}`}>
                  <XCircle className="h-4 w-4" /> Not Ready
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
